import { useState } from "react";

import {
  readItems,
  createItem,
  updateItem,
  updateItems,
  updateUser,
  readUsers,
} from "@directus/sdk";
import { message, Modal } from "antd";
import { getDirectusClient } from "../../../lib/directus";

export function useSerials({ vendor, loans, onLoansRefresh, userBodega }) {
  const [serialsList, setSerialsList] = useState([]);
  const [serialsPage, setSerialsPage] = useState(1);
  const [serialsTotal, setSerialsTotal] = useState(0);
  const [serialsProductId, setSerialsProductId] = useState(null);
  const [serialsLoanId, setSerialsLoanId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [generalFileList, setGeneralFileList] = useState([]);
  const [loadingGeneralSerials, setLoadingGeneralSerials] = useState(false);
  const [modalSerials, setModalSerials] = useState(false);
  const [modalSummaryVisible, setModalSummaryVisible] = useState(false);
  const [summarySerials, setSummarySerials] = useState([]);
  const [modalNotFoundVisible, setModalNotFoundVisible] = useState(false);
  const [notFoundSerialsList, setNotFoundSerialsList] = useState([]);
  const [notFoundSerialsPage, setNotFoundSerialsPage] = useState(1);
  const notFoundSerialsPageSize = 100;
  const pageSize = 100;

  // Obtener seriales de un producto/prestamo
  const obtenerSerialesProducto = async (
    productoId,
    prestamoId,
    page = 1,
    pageSize = 100
  ) => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "serial",
            "estado",
            "producto.id",
            "producto.grupo.impuesto",
            "producto.preciosinimp",
            "producto.nombre",
          ],
          filter: {
            _and: [
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    id: { _eq: prestamoId },
                  },
                },
              },
              { estado: { _eq: 9 } },
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    vendedor: { _eq: vendor.id },
                  },
                },
              },
              { producto: { id: { _eq: productoId } } },
              { estado: { _neq: 1 } },
            ],
          },
          limit: pageSize,
          offset: (page - 1) * pageSize,
        })
      );
      const responseCount = await directus.request(
        readItems("seriales", {
          filter: {
            _and: [
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    id: { _eq: prestamoId },
                  },
                },
              },
              { estado: { _eq: 9 } },
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    vendedor: { _eq: vendor.id },
                  },
                },
              },
              { producto: { id: { _eq: productoId } } },
              { estado: { _neq: 1 } },
            ],
          },
          aggregate: { countDistinct: ["id"] },
        })
      );
      setSerialsTotal(responseCount[0]?.countDistinct?.id || 0);
      return response.data || response;
    } catch (error) {
      console.error("Error obteniendo seriales:", error);
      message.error("Error al obtener los seriales");
      return [];
    }
  };

  // Handler para ver seriales de un producto/prestamo
  const onViewSerials = async (record) => {
    setSerialsPage(1);
    setSerialsProductId(record.productId);
    setSerialsLoanId(record.loanId);
    const serials = await obtenerSerialesProducto(
      record.productId,
      record.loanId,
      1,
      pageSize
    );
    setSerialsList(serials);
    setModalSerials(true);
  };

  // Handler para paginación de seriales
  const onSerialsPageChange = async (page) => {
    setSerialsPage(page);
    if (serialsProductId && serialsLoanId) {
      const serials = await obtenerSerialesProducto(
        serialsProductId,
        serialsLoanId,
        page,
        pageSize
      );
      setSerialsList(serials);
    }
  };

  // Handler para cerrar modal de seriales
  const onCloseSerialsModal = () => {
    setSerialsList([]);
    setModalSerials(false);
  };

  // Handler para procesar archivo de seriales por producto
  const onUploadSerials = async (file, record) => {
    const serials = await obtenerSerialesProducto(
      record.productId,
      record.loanId,
      1,
      10000
    );
    await processFile(file, serials);
    return false;
  };

  // Procesar archivo de seriales para devolución por producto
  const processFile = async (file, serialsProducto) => {
    try {
      const text = await file.text();
      const serialNumbers = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const serialsReturned = serialsProducto.filter(
        (s) => serialNumbers.includes(s.serial) && s.estado === 1
      );
      if (serialsReturned.length > 0) {
        Modal.warning({
          title: "Seriales ya devueltos",
          content: `Los siguientes seriales ya fueron devueltos: ${serialsReturned
            .map((s) => s.serial)
            .join(", ")}`,
        });
        return;
      }
      const productSerials = new Set(serialsProducto.map((s) => s.serial));
      const invalidSerials = serialNumbers.filter(
        (serial) => !productSerials.has(serial)
      );
      if (invalidSerials.length > 0) {
        Modal.error({
          title: "Seriales inválidos",
          content: `Los siguientes seriales no pertenecen a este préstamo: ${invalidSerials.join(
            ", "
          )}`,
        });
        return;
      }
      const serialsToReturn = serialsProducto.filter((s) =>
        serialNumbers.includes(s.serial)
      );
      const directus = await getDirectusClient();
      let totalToReturn = 0;
      if (serialsToReturn.length > 0) {
        const producto = serialsToReturn[0].producto;
        const precioSinImp = parseFloat(producto.preciosinimp || 0);
        const impuesto = parseFloat(producto.grupo?.impuesto || 0);
        const precioConImp = precioSinImp + (precioSinImp * impuesto) / 100;
        totalToReturn = precioConImp * serialsToReturn.length;
      }
      const returnLoanResponse = await directus.request(
        createItem("prestamos_tropas", {
          vendedor: vendor.id,
          tipo: "devolver prestamo",
          estado: "devolucion",
        })
      );
      if (returnLoanResponse.id) {
        await directus.request(
          updateItem("prestamos_tropas", returnLoanResponse.id, {
            seriales: {
              create: serialsToReturn.map((serial) => ({
                prestamos_id: "+",
                seriales_id: { id: serial.id },
              })),
            },
          })
        );
      }
      if (returnLoanResponse.id) {
        const serialIds = serialsToReturn.map((serial) => serial.id);

        const chunkArray = (arr, size) =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
          );
        const serialChunks = chunkArray(serialIds, 100);
        await Promise.all(
          serialChunks.map((chunk) =>
            directus.request(
              updateItems("seriales", chunk, {
                estado: 1,
                user: null,
              })
            )
          )
        );
        await directus.request(
          updateUser(vendor.id, {
            prestamo_usado:
              parseFloat(vendor.prestamo_usado || 0) - totalToReturn,
          })
        );
        Modal.success({
          title: "Éxito",
          content: `${
            serialsToReturn.length
          } seriales devueltos correctamente y préstamo actualizado: -$${totalToReturn.toFixed(
            2
          )}`,
        });
        // Refresh data
        const updatedVendorData = await directus.request(
          readUsers({
            fields: [
              "*",
              "seriales_prestados.*",
              "seriales_prestados.producto.*",
              "seriales_prestados.producto.grupo.impuesto",
            ],
            filter: {
              id: { _eq: vendor.id },
              bodega: { id: { _eq: userBodega } },
            },
          })
        );
        if (updatedVendorData && updatedVendorData[0]) {
          // setVendor(updatedVendorData[0]); // No se expone aquí, refresca loans
        }
        await onLoansRefresh();
      }
    } catch (error) {
      console.error("Error processing file:", error);
      Modal.error({
        title: "Error",
        content: "Error al procesar el archivo",
      });
    }
  };

  // Handler para devolución de productos no serializados
  const onReturnNotSerialized = async (record) => {
    try {
      const directus = await getDirectusClient();
      const inventarioNSPrestamo = await directus.request(
        readItems("inventario_ns_prestamo", {
          fields: ["*", "inventario_ns.*"],
          filter: {
            _and: [
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    vendedor: {
                      _eq: vendor.id,
                    },
                  },
                },
              },
              {
                inventario_ns: {
                  _eq: record.productId,
                },
              },
              {
                estado: {
                  _eq: "prestado",
                },
              },
            ],
          },
        })
      );
      if (!inventarioNSPrestamo || inventarioNSPrestamo.length === 0) {
        message.error("No se encontró el préstamo del producto");
        return;
      }
      const prestamo = inventarioNSPrestamo[0];
      const cantidadDevuelta = prestamo.cantidad;
      await directus.request(
        updateItem("inventario_no_serial", prestamo.inventario_ns.id, {
          cantidad:
            Number(prestamo.inventario_ns.cantidad) + Number(cantidadDevuelta),
        })
      );
      await directus.request(
        updateItem("inventario_ns_prestamo", prestamo.id, {
          estado: "devolucion",
        })
      );
      const currentPrestamoUsado = parseFloat(vendor.prestamo_usado || 0);
      const newPrestamoUsado = Math.max(
        0,
        currentPrestamoUsado - Number(cantidadDevuelta)
      );

      await directus.request(
        updateUser(vendor.id, {
          prestamo_usado: newPrestamoUsado,
        })
      );
      await directus.request(
        createItem("prestamos_tropas", {
          vendedor: vendor.id,
          tipo: "devolver prestamo",
          estado: "devolucion",
          inventario_nsp: {
            create: [
              {
                inventario_ns_prestamo_id: prestamo.id,
              },
            ],
          },
        })
      );
      message.success("Producto devuelto correctamente");
      await onLoansRefresh();
    } catch (error) {
      console.error("Error al devolver producto:", error);
      message.error("Error al devolver el producto");
    }
  };

  // Handler para subir archivo de seriales a nivel general
  const onGeneralSerialsUpload = async (file) => {
    setLoadingGeneralSerials(true);
    try {
      const text = await file.text();
      const serialNumbers = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      let directus = await getDirectusClient();
      const loansIds = loans.map((p) => p.id);
      const serialsAll = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "serial",
            "estado",
            "producto.id",
            "producto.nombre",
            "producto.preciosinimp",
            "producto.grupo.impuesto",
            "prestamo.prestamos_id.id",
          ],
          filter: {
            _and: [
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    id: { _in: loansIds },
                  },
                },
              },
              { estado: { _eq: 9 } },
              {
                prestamos_tropas: {
                  prestamos_tropas_id: {
                    vendedor: { _eq: vendor.id },
                  },
                },
              },
              { estado: { _neq: 1 } },
            ],
          },
          limit: -1,
        })
      );
      const serialMap = {};
      serialsAll.forEach((s) => {
        serialMap[s.serial] = s;
      });
      const serialsToReturn = [];
      const summary = {};
      const notFoundSerials = [];
      serialNumbers.forEach((serial) => {
        const s = serialMap[serial];
        if (s) {
          serialsToReturn.push(s);
          const key = `${s.producto.id}`;
          if (!summary[key]) {
            summary[key] = {
              producto: s.producto.nombre,
              productoId: s.producto.id,
              cantidad: 0,
            };
          }
          summary[key].cantidad += 1;
        } else {
          notFoundSerials.push(serial);
        }
      });
      if (serialsToReturn.length === 0) {
        setNotFoundSerialsList(notFoundSerials);
        setNotFoundSerialsPage(1);
        setModalNotFoundVisible(true);
        setLoadingGeneralSerials(false);
        return;
      }
      const serialsByLoan = {};
      serialsToReturn.forEach((s) => {
        let loanId = null;
        if (Array.isArray(s.prestamo)) {
          const found = s.prestamo.find(
            (p) => p && p.prestamos_id && p.prestamos_id.id
          );
          if (found) loanId = found.prestamos_id.id;
        } else if (
          s.prestamo &&
          s.prestamo.prestamos_id &&
          s.prestamo.prestamos_id.id
        ) {
          loanId = s.prestamo.prestamos_id.id;
        }
        if (!loanId) return;
        if (!serialsByLoan[loanId]) serialsByLoan[loanId] = [];
        serialsByLoan[loanId].push(s);
      });
      let totalToReturn = 0;

      for (const loanId in serialsByLoan) {
        const serials = serialsByLoan[loanId];
        const returnLoanResponse = await directus.request(
          createItem("prestamos_tropas", {
            vendedor: vendor.id,
            tipo: "devolver prestamo",
            estado: "devolucion",
          })
        );
        if (returnLoanResponse.id) {
          await directus.request(
            updateItem("prestamos_tropas", returnLoanResponse.id, {
              seriales: {
                create: serials.map((serial) => ({
                  prestamos_id: "+",
                  seriales_id: { id: serial.id },
                })),
              },
            })
          );
          const serialIds = serials.map((serial) => serial.id);
          const chunkArray = (arr, size) =>
            Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
              arr.slice(i * size, i * size + size)
            );
          const serialChunks = chunkArray(serialIds, 100);
          await Promise.all(
            serialChunks.map((chunk) =>
              directus.request(
                updateItems("seriales", chunk, {
                  estado: 1,
                  user: null,
                })
              )
            )
          );

          // Calcular el total a devolver para este grupo de seriales
          if (serials.length > 0) {
            const producto = serials[0].producto;
            const precioSinImp = parseFloat(producto.preciosinimp || 0);
            const impuesto = parseFloat(producto.grupo?.impuesto || 0);
            const precioConImp = precioSinImp + (precioSinImp * impuesto) / 100;
            totalToReturn += precioConImp * serials.length;
          }
        }
      }

      // Actualizar el prestamo_usado del vendedor
      if (totalToReturn > 0) {
        const currentPrestamoUsado = parseFloat(vendor.prestamo_usado || 0);
        const newPrestamoUsado = Math.max(
          0,
          currentPrestamoUsado - totalToReturn
        );

        await directus.request(
          updateUser(vendor.id, {
            prestamo_usado: newPrestamoUsado,
          })
        );
      }
      setSummarySerials(Object.values(summary));
      setModalSummaryVisible(true);
      setGeneralFileList([]);
      await onLoansRefresh();
      setLoadingGeneralSerials(false);
    } catch (error) {
      console.error("Error al procesar seriales generales:", error);
      Modal.error({
        title: "Error",
        content: "Error al procesar el archivo de seriales",
      });
      setLoadingGeneralSerials(false);
    }
  };

  // Handlers para cerrar modales
  const onCloseSummaryModal = () => setModalSummaryVisible(false);
  const onCloseNotFoundModal = () => setModalNotFoundVisible(false);

  return {
    serialsList,
    setSerialsList,
    serialsPage,
    setSerialsPage,
    serialsTotal,
    setSerialsTotal,
    serialsProductId,
    setSerialsProductId,
    serialsLoanId,
    setSerialsLoanId,
    fileList,
    setFileList,
    generalFileList,
    setGeneralFileList,
    loadingGeneralSerials,
    setLoadingGeneralSerials,
    modalSerials,
    setModalSerials,
    modalSummaryVisible,
    setModalSummaryVisible,
    summarySerials,
    setSummarySerials,
    modalNotFoundVisible,
    setModalNotFoundVisible,
    notFoundSerialsList,
    setNotFoundSerialsList,
    notFoundSerialsPage,
    setNotFoundSerialsPage,
    notFoundSerialsPageSize,
    onViewSerials,
    onUploadSerials,
    onReturnNotSerialized,
    onGeneralSerialsUpload,
    onCloseSerialsModal,
    onSerialsPageChange,
    onCloseSummaryModal,
    onCloseNotFoundModal,
  };
}
